import { data, useLoaderData, type LoaderFunctionArgs, type MetaFunction } from 'react-router'
import { requestOptions } from '@/lib/http'
import CategoryHeader from '@/site/components/CategoryHeader'
import SearchView from '@/site/components/SearchView'
import { fetchCollection } from '@/site/lib/collections'
import { HITS_PER_PAGE } from '@/site/lib/search'
import { isSearchVariation, knownSearchParams, listingCanonicalPath, loadSearchResults, searchStateFromParams } from '@/site/lib/searchPage'
import {
  absoluteUrl,
  breadcrumbLd,
  describe,
  errorMeta,
  NOINDEX_FOLLOW,
  pageSuffix,
  seoMeta,
  SITE_NAME,
  siteUrlOf,
} from '@/site/lib/seo'
import { hitPath } from '@/site/lib/tutorialHit'

/**
 * Collection landing page (/:slug), managed in the control panel: hero, intro and the search results
 * pre-filtered by the collection's facet filter. Unknown or unpublished slugs are a real 404 (v1 showed the
 * unfiltered results, a soft 404).
 */
export async function loader({ request, params }: LoaderFunctionArgs) {
  const opts = requestOptions(request)
  const collection = await fetchCollection(params.slug ?? '', opts)
  if (!collection) throw data(null, { status: 404 })

  const url = new URL(request.url)
  const filter = collection.filter ? { filter: collection.filter.facet, value: collection.filter.value } : {}
  const results = await loadSearchResults(url.searchParams, filter, opts)
  return { collection, results }
}

export const meta: MetaFunction<typeof loader> = ({ loaderData, location, matches, error }) => {
  if (!loaderData || error) return errorMeta(404)

  const { collection, results } = loaderData
  const siteUrl = siteUrlOf(matches)
  const params = knownSearchParams(new URLSearchParams(location.search), results.facets)
  const { page } = searchStateFromParams(params)
  const path = `/${collection.slug}`
  const title = (collection.seo_title || `${collection.title} | ${SITE_NAME}`).replace(/( \| .*)?$/, (suffix) => `${pageSuffix(page)}${suffix}`)
  const description = collection.meta_description || describe(collection.intro_html)

  return seoMeta({
    title,
    description,
    siteUrl,
    path: listingCanonicalPath(path, params),
    image: collection.hero_image_url,
    robots: isSearchVariation(params) ? NOINDEX_FOLLOW : undefined,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: collection.title,
        description,
        url: absoluteUrl(siteUrl, path),
        ...(collection.hero_image_url ? { image: collection.hero_image_url } : {}),
        mainEntity: {
          '@type': 'ItemList',
          numberOfItems: results.totalResults,
          itemListElement: results.hits.map((hit, i) => ({
            '@type': 'ListItem',
            position: (results.currentPage - 1) * HITS_PER_PAGE + i + 1,
            url: absoluteUrl(siteUrl, hitPath(hit)),
          })),
        },
      },
      breadcrumbLd(siteUrl, [
        { name: SITE_NAME, path: '/' },
        { name: collection.title, path },
      ]),
    ],
  })
}

export default function Category() {
  const { collection, results } = useLoaderData<typeof loader>()
  return (
    <>
      <CategoryHeader imageUrl={collection.hero_image_url} title={collection.title} introHtml={collection.intro_html} />
      <SearchView results={results} heading={false} />
    </>
  )
}
